import { useRef } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import { FormError } from "./Form/FormError";
import { TextInput } from "./Form/TextInput";
import * as Styled from "./VerificationInputs.styled";

type VerificationInputsProps = {
    numberOfInputs?: number;
    name?: string;
    disabled?: boolean;
};

export const VerificationInputs: React.FC<VerificationInputsProps> = ({
    numberOfInputs = 8,
    name = "verificationCode",
    disabled = false,
}) => {
    const { control, setValue, getValues } = useFormContext();
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const { fieldState } = useController({ name });
    const { error } = fieldState;

    // Next field focus
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const value = e.target.value.replace(/\D/g, ""); //Digts only
        setValue(`${name}[${idx}]`, value);

        if (value && idx < numberOfInputs - 1) {
            inputsRef.current[idx + 1]?.focus();
        }
    };

    // Previous field focus on Backspace
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        if (e.key === "Backspace" && !getValues(`${name}[${idx}]`) && idx > 0) {
            setValue(`${name}[${idx - 1}]`, "");
            inputsRef.current[idx - 1]?.focus();
        }
    };

    return (
        <>
            <Styled.CodeInputs>
                {Array.from({ length: numberOfInputs }).map((_, idx) => (
                    <Controller
                        key={idx}
                        name={`${name}[${idx}]`}
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => {
                            // Omit ref to avoid passing it to function component (React warning)
                            const fieldWithoutRef: typeof field = { ...field } as any;
                            // @ts-expect-error remove ref not needed for TextInput
                            delete fieldWithoutRef.ref;
                            return (
                                <Styled.CodeInputWrapper>
                                    <TextInput
                                        {...fieldWithoutRef}
                                        error={!!fieldState.error}
                                        disabled={disabled}
                                        isRequired
                                        inputProps={{
                                            maxLength: 1,
                                            style: { textAlign: "center" },
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            ref: (el: HTMLInputElement) => (inputsRef.current[idx] = el),
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                                handleInput(e, idx);
                                                field.onChange(e); // propagate to RHF
                                            },
                                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                handleKeyDown(e, idx);
                                            },
                                        }}
                                        label=""
                                    />
                                </Styled.CodeInputWrapper>
                            );
                        }}
                    />
                ))}
            </Styled.CodeInputs>
            <Styled.ErrorWrapper>
                <FormError error={error} />
            </Styled.ErrorWrapper>
        </>
    );
};
